import { useEffect, useState } from 'react';

import viewOrders from '@/shared/api/fulfilment/orders/viewOrders';
import useDataGridTable from '@/shared/components/dataDisplay/DataGridTable/hooks/useDataGridTable';
import useTableColumnVisibility from '@/shared/components/dataDisplay/DataGridTable/hooks/useTableColumnVisibility';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import FulfilmentOrder from '@/shared/types/fulfilment/order.type';

const tableColumnsList = [
  'fulfilment_id',
  'fulfilment_provider_id',
  'fulfilment_queue_id',
  'in_customer_id',
  'in_order_id',
  'status',
  'tracking_number',
  'ts_created',
  'ts_modified',
];

const tableRequiredColumns = [
  'fulfilment_id',
  'fulfilment_provider_id',
  'tracking_number',
  'shipping_labels',
  'status',
  'ts_created',
  'ts_modified',
];

type Args = {
  orderId: string;
};

const useFulfilmentOrdersTable_v2 = ({ orderId }: Args) => {
  const redirectLogin = useRedirectLogin();

  const { apiRef, model, requestBody } = useDataGridTable();

  const [table, setTable] = useState({
    data: [] as FulfilmentOrder[],
    totalRows: 0,
    isLoading: false,
  });

  const { columns, visibilityModel, setVisibilityModel } =
    useTableColumnVisibility({
      tableColumnsList: tableColumnsList,
      tableRequiredColumns: tableRequiredColumns,
    });

  useEffect(() => {
    const fetchFulfilmentOrders = async () => {
      setTable((prev) => ({ ...prev, isLoading: true }));

      const baseSearch =
        requestBody.search.length > 0
          ? [
              {
                in_order_id: {
                  cop: ['='],
                  value: orderId,
                },
              },
              requestBody.search.map((item, idx) =>
                idx === 0 ? { ...item, op: 'and' } : item,
              ),
            ]
          : [
              {
                in_order_id: {
                  cop: ['='],
                  value: orderId,
                },
              },
            ];

      const response = await viewOrders(
        { ...requestBody, search: baseSearch },
        redirectLogin,
      );

      setTable((prev) => ({ ...prev, isLoading: false }));

      if (response.success) {
        setTable((prev) => ({
          ...prev,
          data: response.data,
          totalRows: response.total_records || 0,
        }));
      }
    };

    void fetchFulfilmentOrders();
  }, [orderId, requestBody, redirectLogin]);

  return {
    apiRef,
    model,
    table,
    columns,
    visibilityModel,
    setVisibilityModel,
  };
};

export default useFulfilmentOrdersTable_v2;
